import type {
  PlatformBillingOrderDetail,
  PlatformBillingOrderRow,
  PlatformCreditPromotionWrite,
  RefundRequest,
} from '../../../../shared-types/src/index.js';

export type Validation<Value, Field extends string> =
  { ok: true; value: Value } | { ok: false; field: Field };

export interface PromotionDraft {
  productId: string;
  title: string;
  finalAmountKrw: string;
  startsAt: string;
  endsAt: string;
}

export type PromotionField = 'productId' | 'finalAmountKrw' | 'startsAt' | 'endsAt';

export interface PartialRefundDraft {
  amountKrw: string;
  revokeCreditMillis: string;
  reason: string;
}

export type RefundField = 'amountKrw' | 'revokeCreditMillis' | 'reason';

export interface LatestRequestCoordinator {
  invalidate(): void;
  run<Value>(input: {
    request: () => Promise<Value>;
    apply: (value: Value) => void;
    onStart?: () => void;
    onSettled?: () => void;
  }): Promise<boolean>;
}

/** Keeps only the latest tenant-scoped response authoritative. Stale failures
 * are ignored too, because they no longer describe the selected tenant. */
export function createLatestRequestCoordinator(): LatestRequestCoordinator {
  let generation = 0;
  return {
    invalidate() {
      generation += 1;
    },
    async run<Value>(input: {
      request: () => Promise<Value>;
      apply: (value: Value) => void;
      onStart?: () => void;
      onSettled?: () => void;
    }): Promise<boolean> {
      const requestGeneration = ++generation;
      input.onStart?.();
      try {
        const value = await input.request();
        if (requestGeneration !== generation) return false;
        input.apply(value);
        return true;
      } catch (error) {
        if (requestGeneration !== generation) return false;
        throw error;
      } finally {
        if (requestGeneration === generation) input.onSettled?.();
      }
    },
  };
}

export function canMutateTenantBilling(input: {
  loading: boolean;
  busy: boolean;
  tenantId: string;
  billingTenantId: string | undefined;
}): boolean {
  return (
    !input.loading &&
    !input.busy &&
    Boolean(input.tenantId) &&
    input.billingTenantId === input.tenantId
  );
}

/** Success is reached only after both the write and its required read-back. */
export async function runMutationWithRefresh(input: {
  write: () => Promise<unknown>;
  refresh: () => Promise<unknown>;
  onSuccess: () => void;
}): Promise<void> {
  await input.write();
  await input.refresh();
  input.onSuccess();
}

export interface PlatformBillingOrderDisplayRow {
  orderId: string;
  tenantId: string;
  userId: string;
  status: PlatformBillingOrderRow['status'];
  amount: string;
  credits: string;
  createdAt: string;
}

function valid<Value, Field extends string>(value: Value): Validation<Value, Field> {
  return { ok: true, value };
}

function invalid<Value, Field extends string>(field: Field): Validation<Value, Field> {
  return { ok: false, field };
}

function parsePositiveInteger(value: string): number | undefined {
  if (!/^\d+$/.test(value.trim())) return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

export function parsePositiveWon(value: string): number | undefined {
  return parsePositiveInteger(value);
}

export function localInputToIso(value: string): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

export function isoToLocalInput(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function parsePromotionDraft(
  draft: PromotionDraft
): Validation<PlatformCreditPromotionWrite, PromotionField> {
  const finalAmountKrw = parsePositiveWon(draft.finalAmountKrw);
  const startsAt = localInputToIso(draft.startsAt);
  const endsAt = localInputToIso(draft.endsAt);
  if (!draft.productId) return invalid('productId');
  if (finalAmountKrw === undefined) return invalid('finalAmountKrw');
  if (!startsAt) return invalid('startsAt');
  if (!endsAt || endsAt <= startsAt) return invalid('endsAt');
  return valid({
    productId: draft.productId,
    title: draft.title.trim(),
    finalAmountKrw,
    startsAt,
    endsAt,
  });
}

export function parsePartialRefundDraft(
  draft: PartialRefundDraft,
  order: PlatformBillingOrderDetail
): Validation<Extract<RefundRequest, { mode: 'partial-used' }>, RefundField> {
  const amountKrw = parsePositiveWon(draft.amountKrw);
  if (amountKrw === undefined || amountKrw > order.cancelableAmountKrw) {
    return invalid('amountKrw');
  }
  const revokeCreditMillis = parsePositiveInteger(draft.revokeCreditMillis);
  if (revokeCreditMillis === undefined || revokeCreditMillis > order.unusedCreditMillis) {
    return invalid('revokeCreditMillis');
  }
  const reason = draft.reason.trim();
  if (!reason) return invalid('reason');
  return valid({
    mode: 'partial-used',
    amountKrw,
    revokeCreditMillis,
    idempotencyKey: '',
    reason,
  });
}

export function fullUnusedRefundRequest(
  reason: string
): Extract<RefundRequest, { mode: 'full-unused' }> {
  return {
    mode: 'full-unused',
    idempotencyKey: '',
    reason: reason.trim(),
  };
}

export function platformBillingOrderRow(
  order: PlatformBillingOrderRow
): PlatformBillingOrderDisplayRow {
  return {
    orderId: order.orderId,
    tenantId: order.tenantId,
    userId: order.redactedUserId,
    status: order.status,
    amount: `${order.finalAmountKrw.toLocaleString('ko-KR')}원`,
    credits: `${(order.creditMillis / 1_000).toLocaleString('ko-KR')} 크레딧`,
    createdAt: order.createdAt.slice(0, 16).replace('T', ' '),
  };
}
